import { Paper, Skeleton, Group } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { centroid } from '@turf/turf';
import { FeatureCollection, MultiPolygon } from 'geojson';
import { MapEvent, MapMouseEvent } from 'mapbox-gl';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IlotPopup, IlotPopupData, useContractsQueries } from '@contracts';
import { PfMap, PlotsLayer, IlotBoundariesLayer, zoomOnFeature } from '@map';
import { TsrButtonLink } from '@ui';

const ILOT_FILL_LAYER_ID = 'ilot-boundaries-fill';
const ILOT_OUTLINE_LAYER_ID = 'ilot-boundaries-outline';

export const Route = createFileRoute('/_app/os/contracts_/$contractId/')({
  component: RouteComponent,
  pendingComponent: () => <Skeleton h="100%" width="100%" />,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { contractId } = Route.useParams();
  const { campaign } = useSearch({ from: '/_app/os/contracts_/$contractId' });
  const mapLoadedRef = useRef(false);
  const [selectedIlotId, setSelectedIlotId] = useState<number | null>(null);
  const [popupData, setPopupData] = useState<IlotPopupData | null>(null);

  const selectedCampaignIds = campaign || [];

  const queries = useContractsQueries();
  const contract = useQuery({
    ...queries.detail(
      Number(contractId),
      selectedCampaignIds.length > 0 ? selectedCampaignIds : undefined,
    ),
    placeholderData: keepPreviousData,
  });

  const handleLoad = (map: MapEvent) => {
    if (contract.data?.boundaries.features.length && !mapLoadedRef.current) {
      zoomOnFeature({
        map: map.target,
        geojson: contract.data.boundaries,
      });
      mapLoadedRef.current = true;
    }
  };

  const handleMapClick = (event: MapMouseEvent) => {
    const features = event.target.queryRenderedFeatures(event.point, {
      layers: [ILOT_FILL_LAYER_ID, ILOT_OUTLINE_LAYER_ID],
    });

    if (features.length > 0) {
      const feature = features[0];
      const ilotId = feature.properties?.id;

      if (ilotId !== undefined) {
        const ilotFeature = contract.data?.ilot_boundaries?.find(
          (ilot) => ilot.properties.id === Number(ilotId),
        );

        if (ilotFeature) {
          // Calculate centroid for popup position
          const center = centroid(ilotFeature.geometry);
          const [longitude, latitude] = center.geometry.coordinates;

          setSelectedIlotId(Number(ilotId));
          setPopupData({
            longitude,
            latitude,
            id: Number(ilotId),
            color: ilotFeature.properties.color,
          });
        }
      }
    } else {
      // Click outside ilot - deselect
      setSelectedIlotId(null);
      setPopupData(null);
    }
  };

  if (!contract.data) {
    return <Skeleton h="100%" width="100%" />;
  }

  const ilotBoundariesData: FeatureCollection<
    MultiPolygon,
    { id?: number; color?: string }
  > | null =
    contract.data.ilot_boundaries && contract.data.ilot_boundaries.length > 0
      ? {
          type: 'FeatureCollection',
          features: contract.data.ilot_boundaries,
        }
      : null;

  return (
    <Paper h={'100%'} p={0} pos="relative">
      <Group pos="absolute" right={12} style={{ zIndex: 2 }} top={12}>
        <TsrButtonLink
          params={{ contractId }}
          to="/os/contracts/$contractId/parcelles"
          variant="filled"
        >
          {t('contracts.view-all-parcelles')}
        </TsrButtonLink>
      </Group>
      <PfMap
        interactiveLayerIds={
          ilotBoundariesData
            ? [ILOT_FILL_LAYER_ID, ILOT_OUTLINE_LAYER_ID]
            : undefined
        }
        onClick={handleMapClick}
        onLoad={handleLoad}
      >
        <PlotsLayer
          data={contract.data.boundaries}
          key={`plots-${selectedCampaignIds.join('-')}`}
        />
        {ilotBoundariesData && (
          <IlotBoundariesLayer
            data={ilotBoundariesData}
            key={`ilot-${selectedCampaignIds.join('-')}`}
            selectedIlotId={selectedIlotId}
          />
        )}
        {popupData && (
          <IlotPopup
            data={popupData}
            onClose={() => {
              setPopupData(null);
              setSelectedIlotId(null);
            }}
          />
        )}
      </PfMap>
    </Paper>
  );
}
